/**
 * Reason codes (wire values, English snake_case like `cursor_expired` and
 * `client_too_old` in spec 9.6 and 9.10) with the Indonesian sentences the sync page
 * (section 16) shows on a problem card: the server's reason after "Ditolak server:" and
 * one sentence on what the device did. Every sentence is checked by the ui-strings lint.
 *
 * Two kinds: a per-op rejection (`RejectReason`, spec 9.5) and a whole-request error
 * (`SyncErrorCode`, spec 9.6, 9.10, 15.2). The policy table says what the device does
 * with the outbox entry and the local row when an op is rejected (spec 9.7 step 2).
 */
import ui from '../strings/ui.id.json' with { type: 'json' }

export const REJECT_REASONS = [
  /** `op.table` is not a synced table of this schema. */
  'unknown_table',
  /** Spec 9.5 step 1: the role may not write this table. */
  'role_cannot_write',
  /** Step 2: the op's parish is not the actor's (no exception applies), or its fields name another. */
  'not_owner',
  /** Step 3: the server row now belongs to another parish (a mutasi was accepted). */
  'row_moved',
  /** Step 3a: outside the ketua lingkungan's own lingkungan, or moving a family. */
  'outside_lingkungan',
  /** Step 4: `op.fields` failed the table's Zod schema. */
  'invalid_fields',
  /** Step 5: the HLC wall clock is more than 24 hours ahead of the server. */
  'clock_ahead',
  /** Step 6: `schema_version` below the supported window (spec 9.10). */
  'client_too_old',
  /** Step 6: `schema_version` above what the server knows; the server needs a deploy. */
  'client_too_new',
  /** Spec 12.2: the same letter number twice in one parish; flagged, never renumbered. */
  'duplicate_letter_number',
  /** Owner, 2026-10-04 (A1): the Catholic KK number of another live family of the parish. */
  'duplicate_kk_number',
  /** Owner, 2026-10-04 (A9): the NIK of another live person. */
  'duplicate_nik',
  /**
   * Release 14 review I-1: the account had its hour's `duplicate_nik` refusals; an op setting a
   * NIK waits, unwritten and unjudged, until the hour ends.
   */
  'rate_limited',
  /** Device: one op alone exceeds the 1 MB push body (spec 9.6). */
  'payload_too_large',
  /** Device: a pulled op could not be written locally (an incomplete creation). */
  'apply_failed',
  /** Device: a document failed to upload five times in a row (spec 10.3). */
  'upload_failed',
  /**
   * Device (release 18 R2-2): a refused parish number SATU cannot give again, the push's
   * `duplicate_kk_number` behind it. The next number would be the refused one (review C-1), or an
   * app before release 18 queued a value that is no parish number (re-review N-4). The change
   * waits; the admin paroki checks the number.
   */
  'kk_number_needs_review',
  /**
   * Release 31 (owner, 2026-11-02): a person or a family deleted while something official still
   * names it (a register entry, an issued letter, an open mutasi, other members), or a membership
   * deleted without its person or family (../hapusData). The deletion is dropped; the row is the
   * server's again.
   */
  'still_referenced',
] as const
export type RejectReason = (typeof REJECT_REASONS)[number]

/** The card of a refused parish number SATU cannot give again (`kk_number_needs_review`). */
export const KK_PERLU_DIPERIKSA = 'kk_number_needs_review' satisfies RejectReason

export const SYNC_ERROR_CODES = [
  /** No valid session for the API; log in again when connected. */
  'unauthorized',
  /** Spec 15.2: wipe local data and show the reason. */
  'user_deactivated',
  'device_revoked',
  'user_revoked',
  /** Spec 9.10: this build is older than the supported window; show the update prompt. */
  'client_too_old',
  /** Spec 9.6, 9.9: the cursor predates the retained ops; take a snapshot. */
  'cursor_expired',
  /** Akun & keamanan (2026-09-30): the password an admin set must be replaced first. */
  'harus_ganti_sandi',
  /** The role or the account's own factor requires an aal2 session: the code, or enrolment. */
  'perlu_verifikasi_dua_langkah',
] as const
export type SyncErrorCode = (typeof SYNC_ERROR_CODES)[number]

/** Codes that end the session on the device (section 05 wipes local data). */
export const WIPE_CODES: ReadonlySet<SyncErrorCode> = new Set([
  'user_deactivated',
  'device_revoked',
  'user_revoked',
])

export function isRejectReason(value: unknown): value is RejectReason {
  return typeof value === 'string' && (REJECT_REASONS as readonly string[]).includes(value)
}

export function isSyncErrorCode(value: unknown): value is SyncErrorCode {
  return typeof value === 'string' && (SYNC_ERROR_CODES as readonly string[]).includes(value)
}

export interface ReasonText {
  /** Completes "Ditolak server: {alasan}" on the card (lower-case start, full stop). */
  alasan: string
  /** What the device did or will do. */
  tindakan: string
}

const GANTI_VERSI_SERVER =
  'Data di perangkat ini diganti dengan versi server; perubahan Anda tidak dikirim.'
const DISIMPAN_DICOBA_LAGI =
  'Perubahan tetap tersimpan dan akan dicoba lagi pada sinkron berikutnya.'

export const REASON_TEXT: Readonly<Record<RejectReason, ReasonText>> = {
  unknown_table: {
    alasan: 'jenis data ini tidak dikenal server.',
    tindakan: 'Perubahan tidak dikirim dan dihapus dari antrean.',
  },
  role_cannot_write: {
    alasan: 'peran akun ini tidak boleh mengubah data tersebut.',
    tindakan: GANTI_VERSI_SERVER,
  },
  not_owner: {
    alasan: 'data ini milik paroki lain.',
    tindakan: GANTI_VERSI_SERVER,
  },
  row_moved: {
    alasan: 'data ini sudah menjadi milik paroki lain sejak mutasi diterima.',
    tindakan: GANTI_VERSI_SERVER,
  },
  outside_lingkungan: {
    // The device fills in its parish's word for the unit (Sebutan unit terkecil).
    alasan: 'data ini berada di luar {unitKecil} Anda.',
    tindakan:
      'Data di perangkat ini diganti dengan versi server, atau disembunyikan bila tidak dikenal server.',
  },
  invalid_fields: {
    alasan: 'isi perubahan tidak lolos pemeriksaan server.',
    tindakan: 'Perubahan tidak dikirim. Buka rekaman ini, periksa isinya, lalu simpan lagi.',
  },
  clock_ahead: {
    alasan: 'jam perangkat ini lebih dari 24 jam di depan jam server.',
    tindakan: 'Perubahan tetap tersimpan dan akan dicoba lagi. Perbaiki jam perangkat.',
  },
  client_too_old: {
    alasan: 'versi SATU di perangkat ini sudah terlalu lama.',
    tindakan: 'Perubahan tetap tersimpan dan akan dikirim setelah SATU diperbarui.',
  },
  client_too_new: {
    alasan: 'versi SATU di perangkat ini lebih baru daripada server.',
    tindakan: DISIMPAN_DICOBA_LAGI,
  },
  duplicate_letter_number: {
    alasan: 'nomor surat ini dipakai dua kali.',
    tindakan: 'Tidak ada yang diubah otomatis. Periksa surat yang benar dan ubah salah satunya.',
  },
  duplicate_kk_number: {
    // A number a family that moved parish left behind is refused with this reason too (release 14).
    // Release 18: the parish number is SATU's, never typed; the device gives the next one itself
    // (sync/loop `beriNomorParokiLagi`), so the clerk has nothing to change. When it cannot, the
    // card is `kk_number_needs_review`'s (R2-2).
    alasan:
      'nomor KK dari paroki ini sudah dipakai keluarga lain di paroki ini, atau pernah dipakai keluarga yang sudah pindah paroki.',
    tindakan:
      'Perubahan tetap tersimpan di perangkat ini dan dikirim lagi setiap sinkron; SATU memberi keluarga ini nomor berikutnya.',
  },
  duplicate_nik: {
    alasan: 'NIK ini sudah tercatat pada umat lain.',
    tindakan:
      'Perubahan tetap tersimpan di perangkat ini dan dikirim lagi setiap sinkron. Buka data umat ini, periksa NIK-nya (perbaiki atau kosongkan), lalu simpan.',
  },
  rate_limited: {
    alasan: 'terlalu banyak NIK yang sudah tercatat dikirim dari akun ini dalam satu jam.',
    tindakan: 'Data ini tetap tersimpan di perangkat ini dan dikirim lagi otomatis nanti.',
  },
  payload_too_large: {
    alasan: 'perubahan ini terlalu besar untuk dikirim (lebih dari 1 MB).',
    tindakan: 'Perubahan tidak dikirim. Buka rekaman ini dan perkecil isinya.',
  },
  apply_failed: {
    alasan: 'perubahan dari server tidak dapat diterapkan di perangkat ini.',
    tindakan: 'Data lengkapnya diambil pada pengambilan ulang berikutnya.',
  },
  upload_failed: {
    alasan: 'berkas belum berhasil diunggah setelah lima percobaan.',
    tindakan: 'Berkas tetap tersimpan di perangkat ini dan dicoba lagi pada sinkron berikutnya.',
  },
  // Names no number: the card is the admin's cue to check the family (R2-2).
  kk_number_needs_review: {
    alasan: ui.nomorKk.perluDiperiksa.alasan,
    tindakan: ui.nomorKk.perluDiperiksa.tindakan,
  },
  still_referenced: {
    alasan: ui.keluarga.hapus.kartuSinkron.alasan,
    tindakan: ui.keluarga.hapus.kartuSinkron.tindakan,
  },
}

const UNKNOWN_REASON: ReasonText = {
  alasan: 'alasan tidak dikenal oleh versi SATU ini.',
  tindakan: DISIMPAN_DICOBA_LAGI,
}

/** Sentences for a code, including codes a newer server may send that this build lacks. */
export function reasonText(code: string): ReasonText {
  return isRejectReason(code) ? REASON_TEXT[code] : UNKNOWN_REASON
}

export interface RejectPolicy {
  /** `drop`: remove the op from the outbox; `retry`: keep it for a later sync. */
  outbox: 'drop' | 'retry'
  /**
   * `replace`: overwrite the local row with `current_row` when the server sent one, and
   * mark it deleted when it did not (the row is unknown to the server). `keep`: leave
   * the local row alone.
   */
  row: 'replace' | 'keep'
}

/** Spec 9.7 step 2, per reason. Ownership reasons replace the local row. */
export const REJECT_POLICY: Readonly<Record<RejectReason, RejectPolicy>> = {
  unknown_table: { outbox: 'drop', row: 'keep' },
  role_cannot_write: { outbox: 'drop', row: 'replace' },
  not_owner: { outbox: 'drop', row: 'replace' },
  row_moved: { outbox: 'drop', row: 'replace' },
  outside_lingkungan: { outbox: 'drop', row: 'replace' },
  invalid_fields: { outbox: 'drop', row: 'keep' },
  clock_ahead: { outbox: 'retry', row: 'keep' },
  client_too_old: { outbox: 'retry', row: 'keep' },
  client_too_new: { outbox: 'retry', row: 'keep' },
  duplicate_letter_number: { outbox: 'drop', row: 'keep' },
  // Nothing is lost: the op waits for the fix and carries it (sync/ganda `segarkanOpGanda`).
  duplicate_kk_number: { outbox: 'retry', row: 'keep' },
  duplicate_nik: { outbox: 'retry', row: 'keep' },
  // Release 14 review I-1: kept and sent again; judged once the account's hour is over.
  rate_limited: { outbox: 'retry', row: 'keep' },
  payload_too_large: { outbox: 'drop', row: 'keep' },
  apply_failed: { outbox: 'drop', row: 'keep' },
  upload_failed: { outbox: 'drop', row: 'keep' },
  // The change waits for the fix, as `duplicate_kk_number`'s does; nothing is lost.
  kk_number_needs_review: { outbox: 'retry', row: 'keep' },
  // The deletion is dropped and the server's row comes back, as for a refused role.
  still_referenced: { outbox: 'drop', row: 'replace' },
}

const UNKNOWN_POLICY: RejectPolicy = { outbox: 'retry', row: 'keep' }

export function rejectPolicy(code: string): RejectPolicy {
  return isRejectReason(code) ? REJECT_POLICY[code] : UNKNOWN_POLICY
}
